import React, { useEffect, useState } from 'react';
import { aopApi, agentLabel, formatDateTime, AGENT_LABELS } from '../aopApi';

const PREFIX_COLORS = {
  workflow: 'var(--blue)',
  agent: 'var(--purple)',
  tool: 'var(--text-sec)',
  approval: 'var(--orange)',
  report: 'var(--green)',
  job: 'var(--text-dim)',
  figma: 'var(--agent-tim)',
  budget: 'var(--red)',
};

const eventColor = (type) => {
  if (type.includes('failed')) return 'var(--red)';
  if (type.includes('completed') || type.includes('decided')) return 'var(--green)';
  return PREFIX_COLORS[type.split('.')[0]] || 'var(--text-dim)';
};

const pillStyle = { width: 'auto', background: 'var(--pill)', border: 'none', color: 'var(--text-sec)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 500, padding: '5px 28px 5px 10px' };

export default function AuditLog() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterAgent, setFilterAgent] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    aopApi
      .audit({ limit: 200 })
      .then((res) => {
        if (!res.success) throw new Error(res.error || 'Failed to load audit log');
        setEvents(res.events || []);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = events.filter((e) => {
    if (filterAgent !== 'all' && e.agent !== filterAgent) return false;
    if (filterType !== 'all' && !e.type.startsWith(filterType)) return false;
    if (search) {
      const haystack = `${e.message} ${e.workflowRunId || ''} ${e.type}`.toLowerCase();
      if (!haystack.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  return (
    <div>
      <div className="aop-page-header">
        <div>
          <h1 className="aop-page-title">Audit Log</h1>
          <p className="aop-page-subtitle">Immutable event stream — all workflow, tool, approval, and agent events</p>
        </div>
        <div className="aop-page-actions">
          <span className="aop-badge" style={{ background: 'rgba(74,143,247,0.12)', color: 'var(--blue)' }}>Read-only</span>
        </div>
      </div>

      {/* Filters */}
      <div className="aop-filter-bar" style={{ marginBottom: 16 }}>
        <input
          className="aop-pill"
          type="text"
          placeholder="Search by run ID or message…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ background: 'var(--pill)', border: 'none', color: 'var(--text-sec)', fontFamily: 'inherit', fontSize: 11, fontWeight: 500, width: 240, outline: 'none' }}
        />
        <select className="aop-pill" value={filterAgent} onChange={(e) => setFilterAgent(e.target.value)} style={pillStyle}>
          <option value="all">All Agents</option>
          {Object.entries(AGENT_LABELS).map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
        <select className="aop-pill" value={filterType} onChange={(e) => setFilterType(e.target.value)} style={pillStyle}>
          <option value="all">All Event Types</option>
          <option value="workflow">Workflow</option>
          <option value="agent">Agent</option>
          <option value="tool">Tool</option>
          <option value="approval">Approvals</option>
          <option value="report">Reports</option>
          <option value="job">Jobs</option>
        </select>
        <div className="aop-filter-bar-spacer" />
        <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{filtered.length} events</span>
      </div>

      {error && (
        <div style={{ background: 'rgba(255,98,93,0.1)', border: '1px solid rgba(255,98,93,0.25)', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--red)' }}>
          {error}
        </div>
      )}

      {/* Event stream */}
      <div className="aop-card">
        {loading ? (
          <div className="aop-empty">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="aop-empty">
            <div className="aop-empty-icon">◎</div>
            {events.length === 0 ? 'No audit events yet — run a workflow to populate the stream' : 'No events match the current filters'}
          </div>
        ) : filtered.map((ev, i) => (
          <div key={ev._id} className="aop-audit-event">
            <div className="aop-audit-dot-col">
              <div className="aop-audit-dot" style={{ background: eventColor(ev.type) }} />
              {i < filtered.length - 1 && <div className="aop-audit-line" />}
            </div>
            <div className="aop-audit-body">
              <div className="aop-audit-desc">{ev.message}</div>
              <div className="aop-audit-meta">
                <span>{formatDateTime(ev.createdAt)}</span>
                {ev.agent && <span><span className={`aop-agent-tag ${ev.agent}`}>{agentLabel(ev.agent)}</span></span>}
                {ev.workflowRunId && <span style={{ fontFamily: 'monospace', fontSize: 10 }}>{ev.workflowRunId.slice(-8)}</span>}
                <span style={{ color: eventColor(ev.type) }}>{ev.type}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
